import type { TaskDTO } from '@taskos/shared';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * App-wide UI state that keyboard shortcuts, tiles and pages share: which modal is open,
 * which task is "current" for single-key actions (E/C/X/Z/R).
 */
export interface TaskFormState {
  mode: 'create' | 'edit';
  taskId?: string;
  /** Pre-fill for create (e.g. from Quick Add or a preset). */
  initial?: Record<string, unknown>;
  focusSection?: 'reminders' | 'due';
}

interface UIState {
  taskForm: TaskFormState | null;
  openTaskForm: (s: TaskFormState) => void;
  closeTaskForm: () => void;
  followUpFor: TaskDTO | null;
  openFollowUp: (t: TaskDTO) => void;
  closeFollowUp: () => void;
  snoozeFor: TaskDTO | null;
  openSnooze: (t: TaskDTO) => void;
  closeSnooze: () => void;
  searchOpen: boolean;
  setSearchOpen: (v: boolean) => void;
  helpOpen: boolean;
  setHelpOpen: (v: boolean) => void;
  current: TaskDTO | null;
  setCurrent: (t: TaskDTO | null) => void;
  focusQuickAdd: () => void;
  /** Register a focus function that returns true if it could focus (i.e. the input is visible). */
  registerQuickAdd: (fn: () => boolean) => () => void;
  navOpen: boolean;
  setNavOpen: (v: boolean) => void;
}

const Ctx = createContext<UIState | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [taskForm, setTaskForm] = useState<TaskFormState | null>(null);
  const [followUpFor, setFollowUpFor] = useState<TaskDTO | null>(null);
  const [snoozeFor, setSnoozeFor] = useState<TaskDTO | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [current, setCurrent] = useState<TaskDTO | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  // Several Quick Add inputs may be mounted (desktop top bar + mobile); focus the visible one.
  const quickAdd = useRef(new Set<() => boolean>());

  const openTaskForm = useCallback((s: TaskFormState) => setTaskForm(s), []);
  const closeTaskForm = useCallback(() => setTaskForm(null), []);
  const openFollowUp = useCallback((t: TaskDTO) => setFollowUpFor(t), []);
  const closeFollowUp = useCallback(() => setFollowUpFor(null), []);
  const openSnooze = useCallback((t: TaskDTO) => setSnoozeFor(t), []);
  const closeSnooze = useCallback(() => setSnoozeFor(null), []);
  const focusQuickAdd = useCallback(() => {
    for (const fn of quickAdd.current) if (fn()) return;
  }, []);
  const registerQuickAdd = useCallback((fn: () => boolean) => {
    quickAdd.current.add(fn);
    return () => {
      quickAdd.current.delete(fn);
    };
  }, []);

  const value = useMemo(
    () => ({
      taskForm,
      openTaskForm,
      closeTaskForm,
      followUpFor,
      openFollowUp,
      closeFollowUp,
      snoozeFor,
      openSnooze,
      closeSnooze,
      searchOpen,
      setSearchOpen,
      helpOpen,
      setHelpOpen,
      current,
      setCurrent,
      focusQuickAdd,
      registerQuickAdd,
      navOpen,
      setNavOpen,
    }),
    [taskForm, openTaskForm, closeTaskForm, followUpFor, openFollowUp, closeFollowUp, snoozeFor, openSnooze, closeSnooze, searchOpen, helpOpen, current, focusQuickAdd, registerQuickAdd, navOpen],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUI(): UIState {
  const c = useContext(Ctx);
  if (!c) throw new Error('UIProvider missing');
  return c;
}
